import React, { useState, useEffect } from "react";
import {
  Switch,
  FormGroup,
  FormControlLabel,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Stack,
  Box,
  CircularProgress,
  Tooltip,
  IconButton,
} from "@mui/material";
import { styled } from "@mui/system";
import { fetchSmoothReception, updateSmoothReception } from "../Service"; // API関数のインポート
import { green } from "@mui/material/colors";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

const DueText = styled("h1")(() => ({
  "--bg-size": "400%",
  "--color-one": "hsl(15 90% 55%)",
  "--color-two": "hsl(40 95% 55%)",
  fontFamily: "sans-serif",
  fontSize: "1.1rem",
  background:
    "linear-gradient(90deg, var(--color-one), var(--color-two), var(--color-one)) 0 0 / var(--bg-size) 100%",
  color: "transparent",
  backgroundClip: "text",
  "@media (prefers-reduced-motion: no-preference)": {
    animation: "move-bg 8s linear infinite",
  },
  "@keyframes move-bg": {
    to: {
      backgroundPosition: "var(--bg-size) 0",
    },
  },
}));

const SmoothReceptionSwitch = styled((props) => (
  <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
  width: 42,
  height: 26,
  padding: 0,
  "& .MuiSwitch-switchBase": {
    padding: 0,
    margin: 2,
    transitionDuration: "300ms",
    "&.Mui-checked": {
      transform: "translateX(16px)",
      color: "#fff",
      "& + .MuiSwitch-track": {
        backgroundColor: theme.palette.primary.main,
        opacity: 1,
        border: 0,
      },
    },
  },
  "& .MuiSwitch-thumb": {
    boxSizing: "border-box",
    width: 22,
    height: 22,
  },
  "& .MuiSwitch-track": {
    borderRadius: 26 / 2,
    backgroundColor: theme.palette.undefined.main,
    opacity: 1,
    transition: theme.transitions.create(["background-color"], {
      duration: 500,
    }),
  },
}));

const SmoothReceptionSwitchForm = ({
  setOpenAlert,
  setOpenSuccess,
  setErrorMessage,
  setSuccessMessage,
}) => {
  const [checked, setChecked] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [newStatus, setNewStatus] = useState("");
  const [loading, setLoading] = useState("");

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const statusData = await fetchSmoothReception();
        setChecked(statusData?.status === "on");
      } catch (error) {
        setErrorMessage("受付特化状態の取得に失敗しました");
        setOpenAlert(true);
        setTimeout(() => {
          setOpenAlert(false); // 3秒後にメッセージを非表示
        }, 3000);
      }
    };

    fetchStatus();
  }, []);

  const handleChange = (event) => {
    setNewStatus(event.target.checked ? "on" : "off");
    setOpenDialog(true);
  };

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await updateSmoothReception(newStatus);
      setLoading(false);
      setOpenDialog(false);
      setChecked(newStatus === "on");
      setSuccessMessage("受付特化状態が更新されました");
      setOpenSuccess(true);
      setTimeout(() => {
        setOpenSuccess(false); // 3秒後にメッセージを非表示
      }, 3000);
    } catch (error) {
      setLoading(false);
      setOpenDialog(false);
      setErrorMessage("受付特化状態の更新に失敗しました");
      setOpenAlert(true);
      setTimeout(() => {
        setOpenAlert(false); // 3秒後にメッセージを非表示
      }, 3000);
    }
  };

  const handleCancel = () => {
    setOpenDialog(false);
  };

  return (
    <>
      <FormGroup>
        <Stack direction="row" alignItems="center" spacing={0.1}>
          <FormControlLabel
            control={
              <SmoothReceptionSwitch
                sx={{ m: 1 }}
                checked={checked}
                onChange={handleChange}
              />
            }
            label={
              <>
                {checked ? (
                  // <DueText>回答募集中</DueText>
                  <Typography variant="h7">受付特化</Typography>
                ) : (
                  <Typography variant="h7">受付特化</Typography>
                )}
              </>
            }
            labelPlacement="start"
          />
          <Tooltip title="受付をスムーズにするために、ゲストが招待状を開いた時に、QRコードが開かれた状態にします。">
            <IconButton size="small">
              <HelpOutlineIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
        </Stack>
      </FormGroup>

      <Dialog open={openDialog} onClose={handleCancel}>
        <DialogTitle>変更の確認</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            {newStatus === "on"
              ? "受付特化をONにしますか？"
              : "受付特化をOFFにしますか？"}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Stack direction="row" sx={{ mr: 2, mb: 2 }} spacing={1}>
            <Button
              variant="contained"
              onClick={handleCancel}
              color="secondary"
            >
              キャンセル
            </Button>
            <Box sx={{ position: "relative" }}>
              <Button
                variant="contained"
                onClick={handleConfirm}
                disabled={loading}
                color="primary"
              >
                変更する
              </Button>
              {loading && (
                <CircularProgress
                  size={24}
                  sx={{
                    color: green[500],
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    marginTop: "-12px",
                    marginLeft: "-12px",
                  }}
                />
              )}
            </Box>
          </Stack>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SmoothReceptionSwitchForm;
